/**
 * Request scheduler shared by the kitchen screen and the waiter app
 * (feature 334). Pure TypeScript, no dependencies: the copy under
 * kitchen/expo-app/src/lib/sync must stay byte for byte identical.
 *
 * Rules, per request class (a string key such as "orders_sync" or "config"):
 *  - at most one call in flight; a second `run` is refused with `busy`;
 *  - every call gets an AbortSignal and is aborted after a timeout;
 *  - after consecutive failures the next wait grows 1 s, 2 s, 4 s ... up to
 *    30 s, with +/-20% jitter (never above the cap); one success resets it.
 *
 * The scheduler does not loop by itself: the caller asks `nextDelay` how long
 * to wait before the next attempt and sets its own timer.
 */

export type RunResult<T> =
  | { ok: true; value: T }
  | { ok: false; reason: "busy" | "timeout" | "stopped" }
  | { ok: false; reason: "error"; error: unknown };

export interface ClassState {
  inFlight: boolean;
  /** Consecutive failures (timeouts and errors) since the last success. */
  failures: number;
}

export interface SchedulerOptions {
  /** Default per-call timeout. Default 10 000 ms. */
  timeoutMs?: number;
  /** First wait after a failure. Default 1000 ms. */
  baseBackoffMs?: number;
  /** Wait cap. Default 30 000 ms. */
  maxBackoffMs?: number;
  /** Jitter ratio. Default 0.2 (+/-20%). */
  jitter?: number;
  /** Returns a number in [0, 1). Default Math.random. */
  random?: () => number;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (id: unknown) => void;
  now?: () => number;
  /** Called whenever a class changes state (start, success, failure). */
  onChange?: (key: string, state: ClassState) => void;
}

export interface Scheduler {
  run<T>(
    key: string,
    fn: (signal: AbortSignal) => Promise<T>,
    options?: { timeoutMs?: number },
  ): Promise<RunResult<T>>;
  /** Wait before the next attempt: `intervalMs` when healthy, else the backoff. */
  nextDelay(key: string, intervalMs: number): number;
  getState(key: string): ClassState;
  /** Aborts every call in flight and refuses new ones. */
  stop(): void;
}

interface Slot {
  failures: number;
  active?: { abort: () => void };
}

export function createScheduler(options: SchedulerOptions = {}): Scheduler {
  const timeoutMs = options.timeoutMs ?? 10_000;
  const base = options.baseBackoffMs ?? 1000;
  const max = options.maxBackoffMs ?? 30_000;
  const jitter = options.jitter ?? 0.2;
  const random = options.random ?? Math.random;
  const setT = options.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
  const clearT = options.clearTimeout ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>));
  const slots = new Map<string, Slot>();
  let stopped = false;

  const slot = (key: string): Slot => {
    let s = slots.get(key);
    if (!s) {
      s = { failures: 0 };
      slots.set(key, s);
    }
    return s;
  };
  const stateOf = (key: string): ClassState => {
    const s = slot(key);
    return { inFlight: s.active !== undefined, failures: s.failures };
  };
  const notify = (key: string) => options.onChange?.(key, stateOf(key));

  function run<T>(
    key: string,
    fn: (signal: AbortSignal) => Promise<T>,
    callOptions: { timeoutMs?: number } = {},
  ): Promise<RunResult<T>> {
    if (stopped) return Promise.resolve({ ok: false, reason: "stopped" });
    const s = slot(key);
    if (s.active) return Promise.resolve({ ok: false, reason: "busy" });

    const controller = new AbortController();
    return new Promise<RunResult<T>>((resolve) => {
      let settled = false;
      let timer: unknown;
      const finish = (result: RunResult<T>) => {
        if (settled) return;
        settled = true;
        clearT(timer);
        s.active = undefined;
        if (result.ok) s.failures = 0;
        else if (result.reason === "timeout" || result.reason === "error") s.failures += 1;
        resolve(result);
        notify(key);
      };
      s.active = {
        abort: () => {
          controller.abort();
          finish({ ok: false, reason: "stopped" });
        },
      };
      timer = setT(() => {
        controller.abort();
        finish({ ok: false, reason: "timeout" });
      }, callOptions.timeoutMs ?? timeoutMs);
      notify(key);
      let pending: Promise<T>;
      try {
        pending = fn(controller.signal);
      } catch (error) {
        finish({ ok: false, reason: "error", error });
        return;
      }
      pending.then(
        (value) => finish({ ok: true, value }),
        (error) => finish({ ok: false, reason: "error", error }),
      );
    });
  }

  function nextDelay(key: string, intervalMs: number): number {
    const failures = slot(key).failures;
    if (failures === 0) return intervalMs;
    const wait = Math.min(max, base * 2 ** (failures - 1));
    const factor = 1 + (random() * 2 - 1) * jitter;
    return Math.min(max, wait * factor);
  }

  function stop(): void {
    stopped = true;
    for (const s of slots.values()) s.active?.abort();
  }

  return { run, nextDelay, getState: stateOf, stop };
}
